import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt, TrendingDown, TrendingUp, Pause, AlertCircle, ShieldAlert, Activity } from 'lucide-react';
import { v3Api } from '../hooks/useApi';
import SalesAnomalyChart from './charts/SalesAnomalyChart';
import ZeroStreakCalendar from './charts/ZeroStreakCalendar';
import InvoiceGapList from './charts/InvoiceGapList';
import FlightRiskDrilldown from './charts/FlightRiskDrilldown';

const TYPE_ICONS = {
    invoice_gap:            Receipt,
    sales_drop:             TrendingDown,
    sales_spike:            TrendingUp,
    zero_sales_streak:      Pause,
    revenue_share_anomaly:  ShieldAlert,
    volume_anomaly:         Activity,
};

function statusOf(alert) {
    if (alert.resolved) return 'resolved';
    if (alert.acknowledged) return 'acknowledged';
    return 'active';
}

/**
 * Slide-in detail panel. Loads full drill-down via /api/anomalies/:id and
 * dispatches to the appropriate chart based on context.kind.
 *
 * NOTE: backend stores per-alert engine output under data.details. The
 * drill-down endpoint normalizes everything into context.* — so we read
 * context.* here, not data.* (only the raw JSON dump uses data.details).
 */
export default function AnomalyDetailPanel({ alertSummary, onClose, onChange, onToast }) {
    const { t } = useTranslation();
    const [full, setFull] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [ackOpen, setAckOpen] = useState(false);
    const [resolveOpen, setResolveOpen] = useState(false);
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);

    const id = alertSummary?.id || alertSummary?._id;

    const load = useCallback(async () => {
        if (!id) return;
        setLoading(true); setError(null);
        try {
            const res = await v3Api.anomalies.get(id);
            setFull(res?.data || null);
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || t('Failed to load alert'));
        } finally {
            setLoading(false);
        }
    }, [id, t]);

    useEffect(() => { load(); }, [load]);

    // Esc to close
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const alert = full || alertSummary || {};
    const context = alert.context || { kind: 'unavailable', reason: loading ? 'loading' : 'no context' };
    const Icon = TYPE_ICONS[alert.alert_type] || AlertCircle;
    const status = statusOf(alert);
    const sev = alert.severity || 'low';

    const doAcknowledge = async () => {
        setBusy(true);
        try {
            await v3Api.anomalies.acknowledge(id, notes || undefined);
            onToast?.(t('Alert acknowledged'), 'success');
            setAckOpen(false); setNotes('');
            await load();
            onChange?.();
        } catch (e) {
            onToast?.(e?.response?.data?.error || t('Failed to acknowledge'), 'error');
        } finally { setBusy(false); }
    };

    const doResolve = async () => {
        setBusy(true);
        try {
            await v3Api.anomalies.resolve(id, notes || undefined);
            onToast?.(t('Alert resolved'), 'success');
            setResolveOpen(false); setNotes('');
            await load();
            onChange?.();
        } catch (e) {
            onToast?.(e?.response?.data?.error || t('Failed to resolve'), 'error');
        } finally { setBusy(false); }
    };

    const doReopen = async () => {
        if (!window.confirm(t('Reopen this alert?'))) return;
        setBusy(true);
        try {
            await v3Api.anomalies.reopen(id);
            onToast?.(t('Alert reopened'), 'success');
            await load();
            onChange?.();
        } catch (e) {
            onToast?.(e?.response?.data?.error || t('Failed to reopen'), 'error');
        } finally { setBusy(false); }
    };

    return (
        <>
            <div className="v3-anomalies-backdrop" onClick={onClose} />
            <aside className="v3-anomalies-detail-panel" role="dialog" aria-modal="true">
                <div className="v3-anomalies-detail-header">
                    <Icon size={18} strokeWidth={1.75} />
                    <h2>{alert.tenant_name || alert.tenant_id || t('Alert')}</h2>
                    <span className={`v3-cc-dot ${sev}`} aria-hidden="true" />
                    <span className={`v3-anomalies-status-badge ${status}`}>{t(status)}</span>
                    <button className="v3-anomalies-detail-close" onClick={onClose} aria-label={t('Close')}>×</button>
                </div>

                <div className="v3-anomalies-detail-body">
                    {error && <div style={{ color: 'var(--v3-text-danger)', fontSize: 13 }}>{error}</div>}

                    <div className="v3-anomalies-detail-meta">
                        <div><strong>{t('Type')}:</strong> {alert.alert_type || '—'}</div>
                        <div><strong>{t('Severity')}:</strong> {t(sev)}</div>
                        {alert.detected_at && (
                            <div><strong>{t('Detected')}:</strong> {new Date(alert.detected_at).toLocaleString()}</div>
                        )}
                        {alert.message && context.kind !== 'flight_risk_breakdown' && (
                            <div style={{ marginTop: 6 }}>{alert.message}</div>
                        )}
                    </div>

                    <div className="v3-anomalies-detail-section">
                        <h4>{t('Drill-down')}</h4>
                        {loading && <div style={{ fontSize: 12, color: 'var(--v3-text-muted)' }}>{t('Loading…')}</div>}
                        {!loading && context.kind === 'timeseries_with_marker' && (
                            <SalesAnomalyChart context={context} />
                        )}
                        {!loading && context.kind === 'streak_with_calendar' && (
                            <ZeroStreakCalendar context={context} />
                        )}
                        {!loading && context.kind === 'invoice_gap_detail' && (
                            <InvoiceGapList context={context} />
                        )}
                        {!loading && context.kind === 'flight_risk_breakdown' && (
                            <FlightRiskDrilldown context={context} tenantName={alert.tenant_name} />
                        )}
                        {!loading && context.kind === 'unavailable' && (
                            <div style={{ fontSize: 12, color: 'var(--v3-text-muted)' }}>
                                {t('Drill-down context is unavailable')}
                                {context.reason ? `: ${context.reason}` : '.'}
                            </div>
                        )}
                    </div>

                    {/* Workflow trail */}
                    {(alert.acknowledged || alert.resolved) && (
                        <div className="v3-anomalies-detail-section">
                            <h4>{t('Workflow')}</h4>
                            <dl className="v3-anomalies-kv">
                                {alert.acknowledged && (
                                    <>
                                        <dt>{t('Acknowledged')}</dt>
                                        <dd>
                                            {alert.acknowledged_by || t('unknown')}
                                            {alert.acknowledged_at && ` · ${new Date(alert.acknowledged_at).toLocaleString()}`}
                                        </dd>
                                    </>
                                )}
                                {alert.notes && (
                                    <>
                                        <dt>{t('Notes')}</dt>
                                        <dd>{alert.notes}</dd>
                                    </>
                                )}
                                {alert.resolved && (
                                    <>
                                        <dt>{t('Resolved')}</dt>
                                        <dd>{alert.resolved_at ? new Date(alert.resolved_at).toLocaleString() : t('yes')}</dd>
                                    </>
                                )}
                                {alert.resolution_notes && (
                                    <>
                                        <dt>{t('Resolution')}</dt>
                                        <dd>{alert.resolution_notes}</dd>
                                    </>
                                )}
                            </dl>
                        </div>
                    )}

                    <details>
                        <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--v3-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            {t('Raw details')} (data.details)
                        </summary>
                        <pre style={{
                            fontSize: 11, background: 'rgba(0,0,0,0.3)', padding: 10,
                            borderRadius: 6, overflow: 'auto', maxHeight: 200,
                            color: 'var(--v3-text-muted)', marginTop: 8,
                        }}>
                            {JSON.stringify(alert.data?.details ?? alert.data ?? {}, null, 2)}
                        </pre>
                    </details>
                </div>

                <div className="v3-anomalies-detail-footer">
                    {ackOpen && (
                        <>
                            <textarea
                                className="v3-anomalies-detail-textarea"
                                placeholder={t('Notes (optional)')}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                            <div className="v3-anomalies-detail-actions">
                                <button onClick={() => { setAckOpen(false); setNotes(''); }} disabled={busy}>{t('Cancel')}</button>
                                <button className="primary" onClick={doAcknowledge} disabled={busy}>{t('Submit')}</button>
                            </div>
                        </>
                    )}
                    {resolveOpen && (
                        <>
                            <textarea
                                className="v3-anomalies-detail-textarea"
                                placeholder={t('Resolution notes (optional)')}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                            <div className="v3-anomalies-detail-actions">
                                <button onClick={() => { setResolveOpen(false); setNotes(''); }} disabled={busy}>{t('Cancel')}</button>
                                <button className="success" onClick={doResolve} disabled={busy}>{t('Resolve')}</button>
                            </div>
                        </>
                    )}
                    {!ackOpen && !resolveOpen && (
                        <div className="v3-anomalies-detail-actions">
                            {!alert.acknowledged && !alert.resolved && (
                                <button className="primary" onClick={() => setAckOpen(true)} disabled={busy || !id}>
                                    {t('Acknowledge')}
                                </button>
                            )}
                            {!alert.resolved && (
                                <button className="success" onClick={() => setResolveOpen(true)} disabled={busy || !id}>
                                    {t('Resolve')}
                                </button>
                            )}
                            {alert.resolved && (
                                <button onClick={doReopen} disabled={busy || !id}>
                                    {t('Reopen')}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
}
